import _ from 'lodash';
import {createSelector} from 'reselect';

import {buildCustomSnoozeFromDynamicPreset} from '../../../helpers/models/snoozePresetHelpers';
import {ReminderPresets, RRelativeTime} from '../../../models/api/preferencesModel';
import {AppState} from '../../appState';
import {getLocalize} from '../../stateless/internationalizationSelectors';

export function getPreferences(state: AppState) {
  return state.data.preferences;
}

export function getBadgeNamespace(state: AppState) {
  return state.data.preferences.badgeNamespace;
}

export function getDefaultFontStyle(state: AppState) {
  return state.data.preferences.defaultFontStyle;
}

export function getDefaultSendAction(state: AppState) {
  return state.data.preferences.defaultSendAction;
}

export function getDefaultReplyType(state: AppState) {
  return state.data.preferences.defaultReplyType;
}

export function getLastEmailFormat(state: AppState) {
  return state.data.preferences.lastEmailFormat;
}

export function getEmojiSkinTone(state: AppState) {
  return state.data.preferences.emojiSkinTone;
}

export function getMarkAsReadDelay(state: AppState) {
  return state.data.preferences.markAsReadDelay;
}

export function getReselectDirection(state: AppState) {
  return state.data.preferences.reselectDirection;
}

export function getShouldSendOnEnterForChat(state: AppState) {
  return state.data.preferences.shouldSendOnEnterForChat;
}

export function getShouldMuteNotifications(state: AppState) {
  return state.data.preferences.shouldMuteNotifications;
}

export function getShouldHideAddIndividualInbox(state: AppState) {
  return state.data.preferences.shouldHideAddIndividualInbox;
}

export function getDefaultChannelId(state: AppState) {
  return state.data.preferences.defaultChannelId;
}

export function getShouldReplyWithDefaultChannel(state: AppState) {
  return state.data.preferences.shouldReplyWithDefaultChannel;
}

export function getDoNotDisturb(state: AppState) {
  return state.data.preferences.doNotDisturb;
}

function getRawVisibleInboxIds(state: AppState) {
  return state.data.preferences.visibleInboxIds;
}
export const getVisibleInboxIds = createSelector(getRawVisibleInboxIds, (inboxIds) => new Set(inboxIds));

export const getIsInboxVisible = createSelector(
  [getVisibleInboxIds],
  (visibleInboxIds) => (inboxId: number) => visibleInboxIds.has(inboxId),
);

function getRawHiddenDelegationOwnerIds(state: AppState) {
  return state.data.preferences.hiddenDelegationOwnerIds;
}
export const getHiddenDelegationOwnerIds = createSelector(
  getRawHiddenDelegationOwnerIds,
  (delegationIds) => new Set(delegationIds),
);

export function getDisplayedTeammateIds(state: AppState) {
  return state.data.preferences.displayedTeammateIds || [];
}

export function getHiddenFrameIds(state: AppState) {
  return state.data.preferences.hiddenFrameIds;
}

export function getSortedFrameIds(state: AppState) {
  return state.data.preferences.sortedFrameIds;
}

export function getVisibleCustomViewIds(state: AppState) {
  return state.data.preferences.visibleCustomViewIds;
}

export function getReminderValues(state: AppState) {
  return state.data.preferences.reminderValues;
}

export function getReminderPresets(state: AppState): ReminderPresets {
  return state.data.preferences.reminderPresets;
}

export function getCustomSnoozes(state: AppState) {
  return state.data.preferences.customSnoozes;
}

// TODO: deprecate `reminderValues` and `reminderPresets` in favor of `customSnoozes`
export const getCustomSnoozesCombined = createSelector(
  [getCustomSnoozes, getReminderPresets, getLocalize],
  (baseCustomSnoozes, reminderPresets, localize) => {
    if (baseCustomSnoozes === null) {
      return _(reminderPresets)
        .toPairs()
        .map(
          (pair) =>
            RRelativeTime.is(pair[1]) && buildCustomSnoozeFromDynamicPreset(pair[1], pair[0], localize),
        )
        .compact()
        .value();
    }

    return baseCustomSnoozes;
  },
);

export function getSendLaterValues(state: AppState) {
  return state.data.preferences.sendLaterValues;
}

export function getCountersVisibilities(state: AppState) {
  return state.data.preferences.countersVisibilities;
}

export function getIsReadOnlyAccount(state: AppState) {
  return state.data.preferences.isReadOnlyAccount;
}

export function getQuickResponsesIds(state: AppState) {
  return state.data.preferences.quickReponsesIds;
}

export function getShouldReplyInNewConversation(state: AppState) {
  return state.data.preferences.shouldReplyInNewConversation;
}

export function getHasSeenWelcomeCarousel(state: AppState) {
  return state.data.preferences.hasSeenWelcomeCarousel;
}

export function getPasteAsText(state: AppState) {
  return state.data.preferences.pasteAsText;
}

export function getDefaultNumberPrefix(state: AppState) {
  return state.data.preferences.defaultNumberPrefix;
}

export function getKeyboardShortcutsType(state: AppState) {
  return state.data.preferences.keyboardShortcutsType;
}

export function getShouldWarnManyRecipients(state: AppState) {
  return state.data.preferences.shouldWarnManyRecipients;
}

export function getShouldWarnMissingAttachments(state: AppState) {
  return state.data.preferences.shouldWarnMissingAttachments;
}

export function getWasOnboardingCompleted(state: AppState) {
  return state.data.preferences.wasOnboardingCompleted;
}

export function getTheme(state: AppState) {
  return state.data.preferences.theme;
}

export function getShouldAlphabetizeWorkspace(state: AppState) {
  return state.data.preferences.shouldAlphabetizeWorkspace;
}

export function getTimeFormat(state: AppState) {
  return state.data.preferences.timeFormat;
}

export function getShouldSubscribeOnReply(state: AppState) {
  return state.data.preferences.shouldSubscribeOnReply;
}

export function getShouldSubscribeOnComment(state: AppState) {
  return state.data.preferences.shouldSubscribeOnComment;
}

export function getSeenOnboardingTips(state: AppState) {
  return state.data.preferences.seenOnboardingTips;
}

export function getGuidedDemoConversationId(state: AppState) {
  return state.data.preferences.guidedDemoConversationId;
}

export function getShouldShowNewTagInWorkspaces(state: AppState) {
  return state.data.preferences.shouldShowNewTagInWorkspaces;
}

export function getShouldShowNewTagInCells(state: AppState) {
  return state.data.preferences.shouldShowNewTagInCells;
}

export function getShouldHideExternalImages(state: AppState) {
  return state.data.preferences.shouldHideExternalImages;
}

export function getShouldUseOpenFilterForTeams(state: AppState) {
  return state.data.preferences.shouldUseOpenFilterForTeams;
}

export function getHasSeenTeamTabsAlert(state: AppState) {
  return state.data.preferences.hasSeenTeamTabsAlert;
}

export function ignoreQuickRuleTagIds(state: AppState) {
  return state.data.preferences.ignoreQuickRuleTagIds;
}

export function getShouldCalendarDisplayWeekends(state: AppState) {
  return state.data.preferences.shouldCalendarDisplayWeekends;
}

export function getCalendarWeekStartDay(state: AppState) {
  return state.data.preferences.calendarWeekStartDay;
}

export function getDefaultCalendarVersionId(state: AppState) {
  return state.data.preferences.defaultCalendarVersionId;
}

export function getLastAutoRecommendation(state: AppState) {
  return state.data.preferences.lastAutoRecommendation;
}

export function getLastEditModeHelpTip(state: AppState) {
  return state.data.preferences.lastEditModeHelpTip;
}

export function getCompletedStartGuideTasks(state: AppState) {
  return state.data.preferences.completedStartGuideTasks;
}

export function getHasSeenDomainLoginBanner(state: AppState) {
  return state.data.preferences.hasSeenDomainLoginBanner;
}

export function getDismissedAlerts(state: AppState) {
  return state.data.preferences.dismissedAlerts;
}

export function getRecentAnswerIds(state: AppState) {
  return state.data.preferences.recentAnswerIds;
}

export function getCalendarTimezone(state: AppState) {
  return state.data.preferences.calendarTimezone;
}

export function getAnalyticsDefaultViews(state: AppState) {
  return state.data.preferences.analyticsDefaultViews;
}

export function getAnalyticsPinnedViews(state: AppState) {
  return state.data.preferences.analyticsPinnedViews;
}

export function getIsBillingContact(state: AppState) {
  return state.data.preferences.isBillingContact;
}

export function getListAutoFormatting(state: AppState) {
  return state.data.preferences.listAutoFormatting;
}

export function getHasDismissedAccountsSetupGuide(state: AppState) {
  return state.data.preferences.hasDismissedAccountsSetupGuide;
}

export function getDefaultVideoConferencing(state: AppState) {
  return state.data.preferences.defaultVideoConferencing;
}

export function getIsVisitorSubscribedAll(state: AppState) {
  return state.data.preferences.isVisitorSubscribedAll;
}

export function getLeftToRightSwipeAction(state: AppState) {
  return state.data.preferences.leftToRightSwipeAction;
}

export function getRightToLeftSwipeAction(state: AppState) {
  return state.data.preferences.rightToLeftSwipeAction;
}

export function getQuickReplyEnabled(state: AppState) {
  return state.data.preferences.quickReplyEnabled;
}

export function getBackButtonOpensP1(state: AppState) {
  return state.data.preferences.backButtonOpensP1;
}

export function getIsGrammarlyEnabled(state: AppState) {
  return state.data.preferences.grammarlyEnabled;
}

export function getIsInstantReschedulingLinkEnabled(state: AppState) {
  return state.data.preferences.instantReschedulingLinkEnabled;
}
